import { lazy, Suspense, useMemo } from 'react';
import { enlaceAlDocumento, formatearCita, referenciasDe, idDeLaReferencia, resolverGrafico, type Grafico } from '@athena/shared';
import { textoConLaFuente, etiquetaDelEje } from './formatoGrafico.js';
export { etiquetaDelEje, techoDeLaEscala } from './formatoGrafico.js';
const Dibujo = lazy(() => import('./DibujoGrafico.js'));
import { TextoConMarcas } from './TextoConMarcas.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';
import { ENLACE_A_EDITOR } from '../../../common/utils/enlaces.js';

/** Marco de Athena, citas y alternativa accesible; el motor Recharts se carga solo con gráficos. */
export function GraficoDeHechos({
  grafico,
  respuesta,
  idioma,
}: {
  readonly grafico: Grafico;
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  const t = diccionarioDe(idioma).consulta;
  const hechos = useMemo(() => new Map(respuesta.fuentes.map(f => [f.hechoId, f])), [respuesta.fuentes]);
  const resuelto = resolverGrafico(grafico, hechos);
  if (resuelto === null) return null;

  const datos = [...new Set(referenciasDe(grafico))].map(r => hechos.get(idDeLaReferencia(r))!);
  const fuentes = [...new Map(datos.map(f => [`${f.fuenteSlug}#${String(f.pagina)}`, f])).values()];
  const comun = (valores: readonly (string | null | undefined)[]) => (valores.every(v => v === valores[0]) ? valores[0] : null);
  const aQuien = comun(datos.map(f => f.poblacion));
  const cuando = resuelto.forma === 'linea' ? null : comun(datos.map(f => f.periodo ?? null));
  // El origen visible se añade desde los hechos, no depende del rótulo del modelo.
  const variasFuentes = new Set(datos.map(f => f.fuenteSlug)).size > 1;
  const paraPintar = variasFuentes
    ? {
        ...resuelto,
        puntos: resuelto.puntos.map(p => ({
          ...p,
          etiqueta: `${p.etiqueta} · ${formatearCita({ ...hechos.get(p.hechoId)!, pagina: null, localizador: null }, idioma)}`,
        })),
      }
    : resuelto;
  const quienYCuando = [
    aQuien === null ? null : `${t.transparencia.aQuien}: ${aQuien}`,
    cuando === null ? null : `${t.transparencia.cuando}: ${cuando}`,
  ].filter((x): x is string => x !== null);
  const idiomaDeNumeros = idioma === 'es' ? 'es-ES' : 'en-GB';

  return (
    <figure
      className='min-w-0 m-0 mb-[18px] rounded-lg border border-(--line-strong) bg-(--chat-bg) p-[18px]'
      data-forma={resuelto.forma}
      data-eje={resuelto.eje}
    >
      <figcaption className='mb-[14px]'>
        <span className='block font-(family-name:--mono) text-[11px] tracking-[0.08em] text-(--ink-faint) uppercase'>{t.grafico.etiqueta}</span>
        <span className='mt-[6px] block font-(family-name:--text) text-[15px] leading-[1.45] font-semibold text-(--ink)'>{resuelto.titulo}</span>
        {quienYCuando.length === 0 ? null : (
          <span className='mt-[6px] block font-(family-name:--text) text-[12px] leading-[1.45] text-(--ink-faint)'>{quienYCuando.join(' · ')}</span>
        )}
      </figcaption>

      <Suspense fallback={<p>{t.grafico.verDatos}</p>}>
        <Dibujo resuelto={paraPintar} orientacion={grafico.orientacion} idioma={idiomaDeNumeros} sinDato={t.grafico.sinDato} fuentes={hechos} />
      </Suspense>

      <details className='mt-3 text-[12px] text-(--ink-soft)'>
        <summary className='cursor-pointer'>{t.grafico.verDatos}</summary>
        <table className='mt-2 w-full text-left [overflow-wrap:anywhere]'>
          <caption>{resuelto.titulo}</caption>
          <thead>
            <tr>
              <th scope='col'>{t.grafico.tabla.punto}</th>
              <th scope='col'>{t.grafico.tabla.valor}</th>
            </tr>
          </thead>
          <tbody>
            {paraPintar.puntos.map(p => (
              <tr key={p.hechoId}>
                <th scope='row'>
                  {p.serie ? `${p.serie} · ` : ''}
                  {p.etiqueta}
                </th>
                <td>
                  {p.x ? `X: ${p.x.cifra ?? etiquetaDelEje(p.x.valor, p.x.unidad, p.x.divisa, idiomaDeNumeros)}; Y: ` : ''}
                  {textoConLaFuente(p, resuelto, idiomaDeNumeros)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      {grafico.pie ? (
        <p className='m-0 mt-3 font-(family-name:--text) text-[13px] leading-[1.5] text-(--ink-soft)'>
          <TextoConMarcas texto={grafico.pie} respuesta={respuesta} idioma={idioma} />
        </p>
      ) : null}
      <div className='mt-[14px] border-t border-(--line) pt-[12px]'>
        {fuentes.map(fuente => (
          <p
            key={`${fuente.fuenteSlug}#${String(fuente.pagina)}`}
            className='m-0 mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-(family-name:--text) text-[12px] text-(--ink-faint)'
          >
            <span>
              {t.grafico.fuente}: {formatearCita(fuente, idioma)}
              {respuesta.referencias
                .filter(r => datos.some(f => f.hechoId === r.hechoId && f.fuenteSlug === fuente.fuenteSlug && f.pagina === fuente.pagina))
                .map(r => (
                  <a key={r.n} href={`#f${String(r.n)}-${respuesta.preguntaId}`} className='ml-[6px] text-(--teal-deep) underline underline-offset-2'>
                    [{String(r.n)}]
                  </a>
                ))}
            </span>
            {fuente.urlDocumento === null ? null : (
              <a href={enlaceAlDocumento(fuente.urlDocumento, fuente.pagina)} {...ENLACE_A_EDITOR} className='text-(--teal-deep)'>
                {t.abrirDocumento}
              </a>
            )}
          </p>
        ))}
      </div>
    </figure>
  );
}
